import React, { useState, useEffect, useRef } from 'react';
import { 
    Box, 
    Typography, 
    Paper, 
    TextField, 
    Button, 
    IconButton, 
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Checkbox,
    ListItemText,
    Divider,
    Alert,
    Snackbar
} from '@mui/material';
import { 
    Close as CloseIcon, 
    Flip as FlipIcon, 
    CameraAlt as CameraIcon,
    Info as InfoIcon,
    Star as StarIcon,
    LocationOn as LocationIcon,
    Delete as DeleteIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components to match your CodeIgniter styling
const NotesContainer = styled(Box)(({ theme, darkMode }) => ({
    position: 'fixed',
    bottom: 0,
    right: 0,
    width: 400,
    height: '100vh',
    backgroundColor: darkMode ? '#23272b' : '#f9f9f9',
    border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
    borderRadius: '10px 10px 0 0',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 2000,
    color: darkMode ? '#e0e0e0' : '#000',
    transition: 'all 0.3s ease',
}));

const ChatHeader = styled(Box)(({ theme, darkMode }) => ({
    backgroundColor: '#00A49B',
    color: '#fff',
    padding: 10,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
}));

const ChatBody = styled(Box)(({ theme, darkMode }) => ({
    padding: 10,
    overflowY: 'auto',
    flexGrow: 1,
    zIndex: 10,
    borderLeft: `1px solid ${darkMode ? '#444' : '#ddd'}`,
    borderRight: `1px solid ${darkMode ? '#444' : '#ddd'}`,
    backgroundColor: darkMode ? '#23272b' : '#fff',
}));

const ChatFooter = styled(Box)(({ theme, darkMode }) => ({
    padding: 10,
    borderTop: `1px solid ${darkMode ? '#444' : '#ddd'}`,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: darkMode ? '#23272b' : '#fff',
}));

const ChatMessage = styled(Paper)(({ theme, darkMode }) => ({
    backgroundColor: darkMode ? '#23272b' : '#f9f9f9',
    borderRadius: 8,
    margin: '8px 0',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    transition: 'box-shadow 0.3s ease',
    position: 'relative',
    padding: 16,
    color: darkMode ? '#e0e0e0' : '#000',
    border: `1px solid ${darkMode ? '#444' : '#eee'}`,
    '&:hover': {
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    },
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: -10,
    right: -10,
    backgroundColor: '#00A49B',
    color: 'white',
    width: 20,
    height: 20,
    '&:hover': {
        backgroundColor: '#008a82',
    },
    '& .MuiSvgIcon-root': {
        fontSize: 14,
    },
}));

const SearchInput = styled(TextField)(({ theme, darkMode }) => ({
    '& .MuiOutlinedInput-root': {
        backgroundColor: 'transparent',
        border: 'none',
        borderBottom: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}`,
        color: darkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)',
        '& fieldset': {
            border: 'none',
        },
        '& input': {
            color: darkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)',
            '&::placeholder': {
                color: darkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)',
                opacity: 1,
            },
        },
    },
}));

const ImageGallery = styled(Box)(({ theme }) => ({
    '& img': {
        display: 'block',
        width: '100%',
        height: 'auto',
        transition: 'transform 0.3s ease',
        cursor: 'pointer',
        borderRadius: 8,
        border: '1px solid #eee',
        background: '#fff',
    },
    '& img:hover': {
        transform: 'scale(1.05)',
    },
}));

const MetaTagFloater = styled(Box)(({ theme }) => ({
    position: 'absolute',
    bottom: 0,
    right: 0,
    background: 'rgba(0, 0, 0, 0.45)',
    color: '#fff',
    padding: 10,
    fontSize: '0.8em',
    borderRadius: '0 0 8px 0',
}));

const AssessmentNotesLine = styled(Box)(({ theme, darkMode }) => ({
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    borderBottom: `inset 1px ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
}));

const AssessmentNotesLabel = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    marginRight: '0.5rem',
}));

const GreenStars = styled(Box)(({ theme }) => ({
    color: '#00A49B',
}));

// Helper functions
function formatDate(ts) {
    if (!ts) return '';
    let date;
    if (!isNaN(ts)) {
        if (String(ts).length > 10) ts = String(ts).slice(0, 10);
        date = new Date(Number(ts) * 1000);
    } else {
        date = new Date(ts);
    }
    const minutes = date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes();
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()} ${date.getHours()}:${minutes}`;
}

function formatLineBreaks(text) {
    if (!text) return '';
    return text.split('\n').map((line, i) => (
        <React.Fragment key={i}>
            {line}
            {i < text.split('\n').length - 1 && <br />}
        </React.Fragment>
    ));
}

function convertMysqlDateToAus(date) {
    try {
        let dateParts = date.split("-");
        return dateParts[2] + "/" + dateParts[1] + "/" + dateParts[0];
    } catch (e) {
        return date;
    }
}

function cleanValue(val, fallback = '') {
    if (val === undefined || val === null) return fallback;
    if (typeof val === 'string') {
        if (val.trim().toLowerCase() === 'undefined' || val.trim().toLowerCase() === 'null') return fallback;
    }
    return val;
}

const NoteList = ({ 
    notes = [], 
    onAddNote, 
    onDeleteNote, 
    onAddImage, 
    subject = null, 
    subjectId = null,
    darkMode = false,
    tagData = { machines: [], service_calls: [], venues: [], users: [] } // users optional for name lookup
}) => {
    // Remove isFlipped
    const [searchTerm, setSearchTerm] = useState('');
    const [newNote, setNewNote] = useState('');
    const [selectedTags, setSelectedTags] = useState([]);
    const [showImageDialog, setShowImageDialog] = useState(false);
    const [showMetadataDialog, setShowMetadataDialog] = useState(false);
    const [metadataContent, setMetadataContent] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [notes]);

    const filteredNotes = notes.filter(note => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        
        if (note.note && note.note.toLowerCase().includes(searchLower)) return true;
        if (note.account_name && note.account_name.toLowerCase().includes(searchLower)) return true;
        if (note.details && typeof note.details === 'string' && note.details.toLowerCase().includes(searchLower)) return true;
        
        return false;
    })
    // Filter out access restriction notes
    .filter(note => !note.access_restriction_pk);

    const formatTags = (tags) => {
        try {
            const parsedTags = typeof tags === 'string' ? JSON.parse(tags) : tags;
            if (!Array.isArray(parsedTags) || !parsedTags.length) return null;
            
            const makeLabel = (tag) => {
                if (tag.foreign_subject === 'machine') {
                    let machine = tagData.machines.find(m => String(m.machine_id_pk) === String(tag.foreign_id));
                    return `Machine: ${machine?.serial_id}`;
                }
                if (tag.foreign_subject === 'service_call') {
                    let call = tagData.service_calls.find(c => String(c.service_call_id) === String(tag.foreign_id));
                    return `Call: ${call?.bc_call_code}`;
                }
                if (tag.foreign_subject === 'venue') {
                    let venue = tagData.venues.find(v => String(v.venue_id) === String(tag.foreign_id));
                    return `Venue: ${venue?.venue_name}`;
                }
                return '';
            };
            
            return parsedTags.map((tag, index) => {
                if (tag.foreign_id === null) return null;
                return (
                    <Chip
                        key={index}
                        label={makeLabel(tag)}
                        size="small"
                        sx={{ 
                            backgroundColor: '#00A49B', 
                            color: 'white', 
                            margin: '2px',
                            fontSize: '0.75em'
                        }}
                    />
                );
            }).filter(Boolean);
        } catch (e) {
            console.log("formatTags error", e);
            return null;
        }
    };

    const formatSubject = (item, type = "note") => {
        let subject = item.subject;
        try {
            if (subject === 'machine') {
                let serial = tagData.machines.find(m => String(m.machine_id_pk) === String(item.subject_fk));
                return `Machine: ${serial?.serial_id}`;
            }
            if (subject === 'service_call' || subject === 'service_calls') {
                let call = tagData.service_calls.find(c => String(c.service_call_id) === String(item.subject_fk));
                if (call && call.bc_call_code) {
                    return `Service Call: ${call.bc_call_code}`;
                } else if (item.subject_fk) {
                    return `Service Call: ${item.subject_fk}`;
                } else {
                    return 'Service Call: undefined';
                }
            }
            if (subject === 'venue') {
                let venue = tagData.venues.find(v => String(v.venue_id) === String(item.subject_fk));
                return `Venue: ${venue?.venue_name}`;
            }
            return '';
        } catch (e) {
            console.error("Error formatting subject", e);
            return "";
        }
    };

    const handleAddNote = () => {
        if (!newNote.trim()) return;
        
        const noteData = {
            note: newNote,
            subject: subject,
            subject_id: subjectId,
            tags: selectedTags
        };
        
        onAddNote?.(noteData);
        setNewNote('');
        setSelectedTags([]);
        setSnackbar({ open: true, message: 'Note added successfully', severity: 'success' });
    };

    const handleDeleteNote = (noteId, type = 'note') => {
        if (window.confirm("Are you sure you want to delete this note?")) {
            onDeleteNote?.(noteId, type);
        }
    };

    const handleShowMetadata = (metadata) => {
        try {
            const parsed = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
            setMetadataContent(parsed);
            setShowMetadataDialog(true);
        } catch (e) {
            console.error('Error parsing metadata:', e);
        }
    };

    // Add NoteItem component
    const NoteItem = ({ note, darkMode, tagData, onDeleteNote, handleShowMetadata }) => {
        // For debugging: log the note object
        console.log('NoteItem:', note);
        // --- Formatting helpers ---
        const formattedDate = formatDate(note.created_time);
        const formattedSubject = formatSubject(note);
        const formattedTags = formatTags(note.tags);

        let imageNotes = undefined;
        let imageTags = undefined;
        if (note.meta_data) {
            try {
                const meta = typeof note.meta_data === 'string' ? JSON.parse(note.meta_data) : note.meta_data;
                imageNotes = meta?.notes;
                imageTags = meta?.tags;
            } catch (e) {
                imageNotes = undefined;
                imageTags = undefined;
            }
        }
        // For assessment images
        const getImageUrl = (path) =>
            `//app.bottlecycler.com/api/warehouse/get_img/?img_url=${encodeURIComponent(path)}`;
        const imageUrl = note.image ? getImageUrl(note.image) : undefined;
        const details = note.details ? (
            typeof note.details === 'string' ? (() => { try { return JSON.parse(note.details); } catch { return {}; } })() : note.details
        ) : {};
        const getBool = (v) => v === true || v === 'true';
        const getVal = (v, fallback = '') => cleanValue(v, fallback);
        let completedBy = 'Unknown';
        if (note.completed_by_name) {
            completedBy = cleanValue(note.completed_by_name, 'Unknown');
        } else if (note.completed_by_id_fk && tagData.users) {
            const user = tagData.users.find(u => String(u.account_id) === String(note.completed_by_id_fk));
            completedBy = user ? cleanValue(user.name, 'Unknown') : 'Unknown';
        }
        let location = 'No location';
        if (!isNaN(note.latitude) && !isNaN(note.longitude) && note.latitude && note.longitude) {
            let accuracy = parseFloat(note.accuracy_in_meters).toFixed(1);
            location = (
                <a 
                    href={`https://www.google.com/maps/?q=${note.latitude},${note.longitude}&ll=${note.latitude},${note.longitude}&z=15`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#00A49B', textDecoration: 'none' }}
                >
                    <LocationIcon sx={{ fontSize: 16, marginRight: 0.5 }} />
                    Accuracy(m): {accuracy}
                </a>
            );
        }
        const stars = Array(parseInt(note.machine_rating) || 0).fill(0).map((_, i) => (
            <StarIcon key={i} sx={{ fontSize: 16, color: '#00A49B' }} />
        ));
        const renderDetailLine = (label, value) => {
            const cleaned = cleanValue(value);
            if (!cleaned) return null;
            return (
                <AssessmentNotesLine key={label} darkMode={darkMode}>
                    <AssessmentNotesLabel component="span">{label}</AssessmentNotesLabel>
                    {cleaned}
                </AssessmentNotesLine>
            );
        };
        let images = [];
        if (details.images && typeof details.images === 'string') {
            try { images = JSON.parse(details.images); } catch { images = []; }
        } else if (Array.isArray(details.images)) {
            images = details.images;
        }
        const date = note.date_raised ? new Date(note.date_raised * 1000) : null;
        const formattedSafDate = date ? `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}` : '';
        const status = note.status ? note.status.replace(/-/g, ' ') : '';
        const safType = note.form_use === 'removal' ? 'Removal' : 'Install';

        // --- Render each type ---
        if (note._is_saf || note.saf_id) {
            // SAF
            return (
                <ChatMessage key={`saf-${note.saf_id}`} darkMode={darkMode}>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#00A49B', marginBottom: 1, display: 'block', paddingTop: '5px', paddingBottom: '5px', fontSize: '0.9em' }}>
                        Site Audit Form ({safType})
                    </Typography>
                    <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
                        <strong>Venue:</strong> {note.venue_name || ''}
                    </Typography>
                    <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
                        <strong>Date Raised:</strong> {formattedSafDate}
                    </Typography>
                    <Typography variant="body2" sx={{ marginBottom: 1 }}>
                        <strong>Status:</strong> {status}
                    </Typography>
                    <Button 
                        variant="outlined" 
                        size="small"
                        sx={{ width: '100%', color: '#00A49B', borderColor: '#00A49B', '&:hover': { borderColor: '#008a82', backgroundColor: 'rgba(0, 164, 155, 0.1)' } }}
                        onClick={() => window.open(note.link, '_blank')}
                    >
                        View SAF
                    </Button>
                </ChatMessage>
            );
        }
        if (note.assessment_id) {
            // Assessment
            return (
                <ChatMessage key={note.assessment_id} darkMode={darkMode}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: 1 }}>{completedBy}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ float: 'right' }}>{formattedDate}</Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#00A49B', marginBottom: 1, display: 'block', paddingTop: '5px', paddingBottom: '5px', fontSize: '0.9em' }}>
                        Machine assessment from call: {cleanValue(note.service_call_id_fk)}
                    </Typography>
                    <AssessmentNotesLine darkMode={darkMode}>
                        <GreenStars sx={{ marginRight: 1 }}>{stars}</GreenStars>
                        {location}
                    </AssessmentNotesLine>
                    {renderDetailLine('Cleaned', getBool(details.machine_cleaned) ? 'Yes' : 'No')}
                    {renderDetailLine('Tagged', getBool(details.machine_tagged) ? 'Yes' : 'No')}
                    {renderDetailLine('Tag', getVal(details.machine_tag))}
                    {renderDetailLine('Tag due', getVal(details.machine_tag_due_date))}
                    {renderDetailLine('Tag date', getVal(details.machine_tagged_date))}
                    {renderDetailLine('Bottle count', getVal(details.bottle_count))}
                    {renderDetailLine('Notes', getVal(details.completion_notes))}
                    {renderDetailLine('Previous months bottle count', getVal(details.previous_months_bottle_count))}
                    {images && images.length > 0 && (
                        <Box key="images" sx={{ marginTop: 1 }}>
                            {images.map((img, i) => (
                                <img
                                    key={i}
                                    src={getImageUrl(img.path)}
                                    alt="Assessment image"
                                    style={{ maxHeight: 100, maxWidth: 100, margin: '0.5rem', borderRadius: 4, border: '1px solid #eee' }}
                                    onClick={() => window.open(getImageUrl(img.path), '_blank')}
                                />
                            ))}
                        </Box>
                    )}
                </ChatMessage>
            );
        }
        if (note.access_restriction_pk) {
            // Access Restriction
            let restrictionDetails = 'No venue restrictions provided';
            try {
                let details = JSON.parse(note.details);
                if (note?.restriction_type === "hours") {
                    restrictionDetails = (
                        <Box>
                            <Typography variant="body2">Access restricted to the following hours</Typography>
                            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                                {details.map((hour, index) => {
                                    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                    const formattedDate = days[hour.day];
                                    const startTime = new Date(`2000-01-01T${hour.start}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                                    const endTime = new Date(`2000-01-01T${hour.end}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                                    return (
                                        <li key={index}>{formattedDate} {startTime} - {endTime}</li>
                                    );
                                })}
                            </ul>
                        </Box>
                    );
                }
                if (note?.restriction_type === "call_ahead") {
                    restrictionDetails = (
                        <Box>
                            <Typography variant="body2">Call ahead required</Typography>
                            <Typography variant="body2">{details?.notes}</Typography>
                        </Box>
                    );
                }
                if (note?.restriction_type === "block") {
                    restrictionDetails = (
                        <Box>
                            <Typography variant="body2">Access blocked:</Typography>
                            <Typography variant="body2">{details?.notes}</Typography>
                        </Box>
                    );
                }
            } catch (e) {}
            return (
                <ChatMessage key={note.access_restriction_pk} darkMode={darkMode}>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#00A49B', marginBottom: 1, display: 'block', paddingTop: '5px', paddingBottom: '5px', fontSize: '0.9em' }}>
                        Access Restriction
                    </Typography>
                    <Typography variant="body1" sx={{ marginBottom: 1 }}>{restrictionDetails}</Typography>
                    <Button variant="text" sx={{ width: '100%', color: '#00A49B' }} onClick={() => {/* Handle edit access restrictions */}}>
                        Edit Access Restrictions
                    </Button>
                </ChatMessage>
            );
        }
        if (note.image || (note.meta_data && typeof note.meta_data === 'string' && note.meta_data.includes('image'))) {
            // Image
            return (
                <ChatMessage key={note.image_id} darkMode={darkMode}>
                    {/* Delete button hidden for now */}
                    <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: 1 }}>{note.account_name || 'Unknown'}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ float: 'right' }}>{formattedDate}</Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#00A49B', marginBottom: 1, display: 'block', paddingTop: '5px', paddingBottom: '5px', fontSize: '0.9em' }}>
                        Subject: {formattedSubject}
                    </Typography>
                    <Box sx={{ position: 'relative', marginBottom: 1 }}>
                        <img src={imageUrl} alt="Note image" style={{ maxWidth: '100%', cursor: 'pointer', borderRadius: 8, border: '1px solid #eee', background: '#fff' }} onClick={() => window.open(imageUrl, '_blank')} />
                        <MetaTagFloater>
                            <IconButton size="small" onClick={() => handleShowMetadata(note.meta_data)} sx={{ color: 'white' }}>
                                <InfoIcon fontSize="small" />
                            </IconButton>
                        </MetaTagFloater>
                    </Box>
                    {imageNotes && (<Typography variant="body2" sx={{ marginBottom: 1 }}>{formatLineBreaks(imageNotes)}</Typography>)}
                    {imageTags && (<Box sx={{ marginTop: 1 }}>{formatTags(JSON.stringify(imageTags))}</Box>)}
                </ChatMessage>
            );
        }
        if (note.note_id) {
            const showSubject = formattedSubject && formattedSubject !== 'Machine: undefined' && note.subject !== 'machine';
            return (
                <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: 1 }}>{note.account_name || 'Unknown'}</Typography>
                        {/* Delete button hidden for now */}
                    </Box>
                    {showSubject && (
                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', marginBottom: 1, display: 'block', paddingTop: '5px', paddingBottom: '5px', fontSize: '0.9em' }}>
                            Subject: {formattedSubject}
                        </Typography>
                    )}
                    <Typography variant="body1" sx={{ marginBottom: 1 }}>{formatLineBreaks(note.note)}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ float: 'right' }}>{formatDate(note.created_time)}</Typography>
                    {formattedTags && (<Box sx={{ marginTop: 1 }}>{formattedTags}</Box>)}
                </Paper>
            );
        }
        // Fallback: show JSON
        return (
            <Paper sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: darkMode ? '#23272b' : '#f5f6fa', color: darkMode ? '#e0e0e0' : '#000' }}>
                <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(note, null, 2)}</pre>
            </Paper>
        );
    };

    console.log(notes);
    return (
        <>
            <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Search input hidden for now */}
                <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
                    {filteredNotes.length === 0 ? (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', marginTop: 2 }}>
                            {searchTerm ? 'No notes found matching your search.' : 'No notes found.'}
                        </Typography>
                    ) : (
                        filteredNotes.map((item, idx) => (
                            <NoteItem
                                key={item.note_id || item.assessment_id || item.image_id || item.access_restriction_pk || item.saf_id || idx}
                                note={item}
                                darkMode={darkMode}
                                tagData={tagData}
                                onDeleteNote={onDeleteNote}
                                handleShowMetadata={handleShowMetadata}
                            />
                        ))
                    )}
                    <div ref={messagesEndRef} />
                </Box>
                {/* Create note UI hidden for now */}
                {/*
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
                    <TextField
                        select
                        multiple
                        value={selectedTags}
                        onChange={(e) => setSelectedTags(e.target.value)}
                        input={<OutlinedInput />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                    <Chip key={value} label={value} size="small" />
                                ))}
                            </Box>
                        )}
                        size="small"
                        sx={{ marginBottom: 1 }}
                        placeholder="Select tags"
                    >
                        {tagData.machines.map((machine) => (
                            <MenuItem key={`machine-${machine.machine_id_pk}`} value={`machine-${machine.machine_id_pk}`}>
                                <Checkbox checked={selectedTags.indexOf(`machine-${machine.machine_id_pk}`) > -1} />
                                <ListItemText primary={`Machine ${machine.serial_id}`} />
                            </MenuItem>
                        ))}
                        {tagData.service_calls.map((call) => (
                            <MenuItem key={`call-${call.service_call_id}`} value={`call-${call.service_call_id}`}>
                                <Checkbox checked={selectedTags.indexOf(`call-${call.service_call_id}`) > -1} />
                                <ListItemText primary={`Service Call ${call.bc_call_code}`} />
                            </MenuItem>
                        ))}
                        {tagData.venues.map((venue) => (
                            <MenuItem key={`venue-${venue.venue_id}`} value={`venue-${venue.venue_id}`}>
                                <Checkbox checked={selectedTags.indexOf(`venue-${venue.venue_id}`) > -1} />
                                <ListItemText primary={`Venue ${venue.venue_name}`} />
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        multiline
                        rows={3}
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        placeholder={`Add a note about ${subject || 'this item'}...`}
                        variant="outlined"
                        size="small"
                        sx={{ marginBottom: 1 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            onClick={handleAddNote}
                            disabled={!newNote.trim()}
                            sx={{ flexGrow: 1 }}
                        >
                            Add {subject || 'note'}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => setShowImageDialog(true)}
                        >
                            <CameraIcon />
                        </Button>
                    </Box>
                </Box>
                */}
            </Box>

            {/* Metadata Dialog */}
            <Dialog
                open={showMetadataDialog}
                onClose={() => setShowMetadataDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Image Metadata</DialogTitle>
                <DialogContent>
                    {metadataContent && Object.entries(metadataContent).map(([key, value]) => {
                        if (key === 'thumbnail' || key === 'MarkerNote') return null;
                        return (
                            <Box key={key} sx={{ marginBottom: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                    {key}:
                                </Typography>
                                <Typography variant="body2">
                                    {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                                </Typography>
                            </Box>
                        );
                    })}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowMetadataDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert 
                    onClose={() => setSnackbar({ ...snackbar, open: false })} 
                    severity={snackbar.severity}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NoteList; 